/**
 * Marketing artwork for the login screen, and ONLY the login screen.
 *
 * These live here rather than in `src/ui/icons.tsx` on purpose. That set is the
 * product's nav/action vocabulary — every glyph in it is a control the user can
 * press. Everything below is decoration for one unauthenticated page: a dashed
 * flight path under a heading, a pin for a destinations stat, a headset beside a
 * support line. Adding them to the shared `IconName` union would offer a dozen
 * decorative options at every real call site in the app.
 *
 * All are `aria-hidden`: each one sits beside text that already says what it
 * means, so announcing them would make a screen reader read every stat twice.
 */

type IconProps = { className?: string; size?: number };

function svg(size: number, children: JSX.Element, className?: string): JSX.Element {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

const S = { stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;

/* ── form field affordances ────────────────────────────────────────────────── */

export function MailIcon({ className, size = 20 }: IconProps): JSX.Element {
  return svg(
    size,
    <>
      <rect x="2.5" y="5" width="19" height="14" rx="2.5" {...S} />
      <path d="m3.5 7.5 8.5 6 8.5-6" {...S} />
    </>,
    className,
  );
}

export function LockIcon({ className, size = 20 }: IconProps): JSX.Element {
  return svg(
    size,
    <>
      <rect x="4.5" y="10" width="15" height="10.5" rx="2.5" {...S} />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" {...S} />
      <circle cx="12" cy="15.2" r="1.4" fill="currentColor" stroke="none" />
    </>,
    className,
  );
}

/** Open eye, or struck through when the password is currently visible. */
export function EyeIcon({ off, className, size = 19 }: IconProps & { off: boolean }): JSX.Element {
  return svg(
    size,
    <>
      <path d="M2.5 12s3.6-6.2 9.5-6.2S21.5 12 21.5 12s-3.6 6.2-9.5 6.2S2.5 12 2.5 12Z" {...S} />
      <circle cx="12" cy="12" r="2.8" {...S} />
      {off && <path d="M4 20 20 4" {...S} />}
    </>,
    className,
  );
}

/* ── chrome ────────────────────────────────────────────────────────────────── */

export function HeadsetIcon({ className, size = 17 }: IconProps): JSX.Element {
  return svg(
    size,
    <>
      <path d="M4 13v-1a8 8 0 0 1 16 0v1" {...S} />
      <rect x="2.5" y="13" width="4.5" height="6.5" rx="2" {...S} />
      <rect x="17" y="13" width="4.5" height="6.5" rx="2" {...S} />
      <path d="M20 19.5v.5a3 3 0 0 1-3 3h-2" {...S} />
    </>,
    className,
  );
}

/* ── the one ornament ──────────────────────────────────────────────────────── */

/**
 * The paper plane that rides the dashed rule. Module-private on purpose: it has
 * no meaning on its own, and exporting it would invite a second, unaligned
 * flight motif somewhere else on the page.
 */
function PlaneGlyph({ className, size = 15 }: IconProps): JSX.Element {
  return svg(
    size,
    <path d="M2.01 21 23 12 2.01 3 2 10l15 2-15 2z" fill="currentColor" stroke="none" />,
    className,
  );
}

/**
 * The dashed flight path with a plane at the END of it. The screen's one
 * ornament, used under each feature-card title and under the sign-in card's
 * subline.
 *
 * The plane sits at the RIGHT END, not the middle. The comp draws the rule as a
 * flight path that the plane has just travelled — dashes trailing behind it —
 * and a centred plane instead reads as two short rules with a glyph wedged
 * between them.
 *
 * Colour comes from `currentColor`, so the caller sets it (`text-brand/40`), and
 * the caller also sets the width — the rule stretches to whatever box it is put
 * in. `aria-hidden`: it sits next to text that already says what the card is.
 *
 * Deliberately STATIC. `tailwind.config.ts` carries an `animate-gy-fly` family
 * that travels `stroke-dashoffset` along an SVG path; this is a CSS
 * `border-dashed` rule, not an SVG stroke, and it is not wired to that family.
 * No motion is introduced, so no `prefers-reduced-motion` handling is needed.
 */
export function DashedFlightRule({ className, size = 14 }: IconProps): JSX.Element {
  return (
    <div className={['flex items-center', className].filter(Boolean).join(' ')} aria-hidden="true">
      <span className="h-0 flex-1 border-t-[1.5px] border-dashed border-current" />
      <PlaneGlyph size={size} className="ml-1.5 flex-none -rotate-[18deg]" />
    </div>
  );
}

/* ── the four feature cards ────────────────────────────────────────────────── */

/**
 * The feature and stat glyphs below are FILLED, not stroked.
 *
 * The comp draws each card's mark as a solid red illustration with real visual
 * weight — an agent behind a counter, a globe trailing a plane. The 2px-stroke
 * outlines these replaced read at roughly a third of that density, so at the
 * comp's ~46px they looked like form icons that had been scaled up rather than
 * artwork. `fill` is set per-shape rather than on the `<svg>` because a few
 * pieces (globe meridians, chart baseline) still want to be hairlines.
 */
const F = { fill: 'currentColor' } as const;

/** Top-view airliner, nose up-and-right. Shared by several of the marks. */
const JET = (
  <path
    d="M20.9 3.1a1.9 1.9 0 0 0-2.7 0l-2.4 2.4-7-2.1a.6.6 0 0 0-.6.16l-.9.9a.6.6 0 0 0 .12.94l5.28 3.26-2.5 2.5-2.72-.5a.6.6 0 0 0-.53.17l-.7.7a.6.6 0 0 0 .15.95l3.08 1.68 1.68 3.08a.6.6 0 0 0 .95.15l.7-.7a.6.6 0 0 0 .17-.53l-.5-2.72 2.5-2.5 3.26 5.28a.6.6 0 0 0 .94.12l.9-.9a.6.6 0 0 0 .16-.6l-2.1-7 2.4-2.4a1.9 1.9 0 0 0 0-2.7Z"
    {...F}
  />
);

/** Lead Management — a travel agent behind a counter, plane overhead. */
export function LeadArt({ className, size = 44 }: IconProps): JSX.Element {
  return svg(
    size,
    <>
      <g transform="translate(12.2 -1.4) scale(.52)">{JET}</g>
      <circle cx="9.2" cy="10.4" r="3.1" {...F} />
      <path d="M3.9 17.6a5.3 5.3 0 0 1 10.6 0v.4H3.9Z" {...F} />
      <path d="M1.6 18h20.8v2.2H1.6Z" {...F} />
      <path d="M3.4 20.2h17.2V22H3.4Z" {...F} opacity=".55" />
    </>,
    className,
  );
}

/** Tour Package Management — globe with an orbiting flight path and a case. */
export function TourArt({ className, size = 44 }: IconProps): JSX.Element {
  return svg(
    size,
    <>
      <circle cx="10.4" cy="10.4" r="7.4" {...F} opacity=".18" />
      <circle cx="10.4" cy="10.4" r="7.4" {...S} strokeWidth="1.6" />
      <path d="M10.4 3v14.8M3 10.4h14.8" {...S} strokeWidth="1.3" />
      <path d="M10.4 3c3 3.6 3 11.2 0 14.8-3-3.6-3-11.2 0-14.8Z" {...S} strokeWidth="1.3" />
      <g transform="translate(13.4 11.6) scale(.46)">{JET}</g>
      <path d="M8.2 17.8h8.6a1.2 1.2 0 0 1 1.2 1.2v2.4a1.2 1.2 0 0 1-1.2 1.2H8.2A1.2 1.2 0 0 1 7 21.4V19a1.2 1.2 0 0 1 1.2-1.2Z" {...F} />
      <path d="M11 17.8v-1.2h3.2v1.2" {...S} strokeWidth="1.4" />
    </>,
    className,
  );
}

/** Booking & Operations — an open ticket booklet with a boarding pass. */
export function BookingArt({ className, size = 44 }: IconProps): JSX.Element {
  return svg(
    size,
    <>
      <g transform="translate(13.6 -1.2) scale(.44)">{JET}</g>
      <path d="M2.2 6.6a12.4 12.4 0 0 1 9.3 2v12a12.4 12.4 0 0 0-9.3-2Z" {...F} />
      <path d="M12.5 8.6a12.4 12.4 0 0 1 9.3-2v12a12.4 12.4 0 0 0-9.3 2Z" {...F} opacity=".45" />
      <path d="M12.5 8.6a12.4 12.4 0 0 1 9.3-2v12a12.4 12.4 0 0 0-9.3 2Z" {...S} strokeWidth="1.5" />
      <path d="M14.8 11.4h4.8M14.8 14.2h4.8M14.8 17h3.2" {...S} strokeWidth="1.4" />
    </>,
    className,
  );
}

/** Reports & Analytics — bars under a rising trend arrow. */
export function ReportArt({ className, size = 44 }: IconProps): JSX.Element {
  return svg(
    size,
    <>
      <path d="M3 14.2h3.3v7.2H3ZM9.35 11h3.3v10.4h-3.3ZM15.7 7.4H19v14h-3.3Z" {...F} />
      <path d="M3.4 12.4 8.6 7.6l3.4 2.6 6.2-6" {...S} strokeWidth="1.9" />
      <path d="M14.4 3.4H19v4.6" {...S} strokeWidth="1.9" />
    </>,
    className,
  );
}

/* ── the four stats ────────────────────────────────────────────────────────── */

/** Happy Travellers — a crowd, front row solid and a back row behind it. */
export function TravellersArt({ className, size = 32 }: IconProps): JSX.Element {
  return svg(
    size,
    <>
      <circle cx="5.1" cy="8.4" r="2.5" {...F} opacity=".5" />
      <path d="M.9 18.4a4.2 4.2 0 0 1 8.4 0v.5H.9Z" {...F} opacity=".5" />
      <circle cx="18.9" cy="8.4" r="2.5" {...F} opacity=".5" />
      <path d="M14.7 18.4a4.2 4.2 0 0 1 8.4 0v.5h-8.4Z" {...F} opacity=".5" />
      <circle cx="12" cy="6.9" r="3.4" {...F} />
      <path d="M6.2 19.6a5.8 5.8 0 0 1 11.6 0v1.3H6.2Z" {...F} />
    </>,
    className,
  );
}

/** Destinations — a globe pinned with location markers. */
export function PinArt({ className, size = 32 }: IconProps): JSX.Element {
  return svg(
    size,
    <>
      <circle cx="11" cy="11.6" r="8.4" {...F} opacity=".2" />
      <circle cx="11" cy="11.6" r="8.4" {...S} strokeWidth="1.7" />
      <path d="M11 3.2v16.8M2.6 11.6h16.8" {...S} strokeWidth="1.35" />
      <path d="M11 3.2c3.4 4.1 3.4 12.7 0 16.8-3.4-4.1-3.4-12.7 0-16.8Z" {...S} strokeWidth="1.35" />
      <path d="M17.6 22.6s4.4-3.9 4.4-7a4.4 4.4 0 0 0-8.8 0c0 3.1 4.4 7 4.4 7Z" {...F} />
      <circle cx="17.6" cy="15.4" r="1.6" fill="#fff" stroke="none" />
    </>,
    className,
  );
}

/** Group Tours — a guide with a raised flag leading a group. */
export function GroupArt({ className, size = 32 }: IconProps): JSX.Element {
  return svg(
    size,
    <>
      <circle cx="17.8" cy="7.4" r="2.2" {...F} opacity=".5" />
      <path d="M14.1 17.6a3.7 3.7 0 0 1 7.4 0v1.1h-7.4Z" {...F} opacity=".5" />
      <circle cx="12.4" cy="8.6" r="2.4" {...F} opacity=".72" />
      <path d="M8.4 18a4 4 0 0 1 8 0v1.2h-8Z" {...F} opacity=".72" />
      <circle cx="5.6" cy="7.6" r="2.7" {...F} />
      <path d="M1.2 17.8a4.4 4.4 0 0 1 8.8 0v1.6H1.2Z" {...F} />
      <path d="M9.6 19.4V2.6" {...S} strokeWidth="1.6" />
      <path d="M10.4 3.2h6.2l-1.5 2.1 1.5 2.1h-6.2Z" {...F} />
    </>,
    className,
  );
}

/** Years of Trust — an award rosette with its ribbon tails. */
export function TrustArt({ className, size = 32 }: IconProps): JSX.Element {
  return svg(
    size,
    <>
      <path d="m8.1 15.2-2 6.9 5.9-3.2 5.9 3.2-2-6.9Z" {...F} opacity=".5" />
      <path
        d="M12 1.4l2.1 1.6 2.6-.4 1 2.4 2.4 1-.4 2.6L21.3 12l-1.6 2.1.4 2.6-2.4 1-1 2.4-2.6-.4L12 21.3l-2.1-1.6-2.6.4-1-2.4-2.4-1 .4-2.6L2.7 12l1.6-2.1-.4-2.6 2.4-1 1-2.4 2.6.4Z"
        {...F}
      />
      <path
        d="m12 7.1 1.42 2.88 3.18.46-2.3 2.24.54 3.17L12 14.36l-2.84 1.49.54-3.17-2.3-2.24 3.18-.46Z"
        fill="#fff"
        stroke="none"
      />
    </>,
    className,
  );
}

/**
 * The faint travel doodles printed behind the sign-in card — dotted flight
 * paths, a plane, map pins, a mountain range, and a skyline of world monuments
 * along the bottom edge. Purely decorative, drawn at low opacity so the form
 * always wins the contrast contest.
 */
export function CardWatermark({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      className={className}
      viewBox="0 0 480 640"
      fill="none"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {/*
        The upper doodles are held to `opacity .5` and pulled clear of the card's
        top third. They used to run at .85 across the whole box, which put a map
        pin and a mountain outline directly behind "Welcome Back!" and the first
        field label — legible enough to read as a rendering fault rather than as
        a watermark. The comp keeps this band almost empty and saves the artwork
        for the bottom edge.
      */}
      <g stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" opacity=".5">
        <path d="M34 196c44-42 108-46 152-16" strokeDasharray="5 7" />
        <path d="M296 168c52 8 94 42 110 86" strokeDasharray="5 7" />
        {/* map pin */}
        <path d="M92 180c0 8-9 15-9 15s-9-7-9-15a9 9 0 0 1 18 0Z" />
        <circle cx="83" cy="179" r="3" />
        {/* plane */}
        <path d="M368 214l38-14-9 20 18 8-20 8-6 19-11-17-21 3 13-14z" />
      </g>
      {/* monument skyline along the bottom, as in the design */}
      <g stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" opacity=".7" transform="translate(0,26)">
        <path d="M28 612v-40h46v40M34 572v-14h34v14M40 558l11-14 11 14" />
        <path d="M120 612v-46h40v46M126 566l14-18 14 18M133 612v-22h14v22" />
        <path d="M196 612v-30h34v30M200 582l17-20 17 20M213 612v-16h4v16" />
        <path d="M268 612c0-26 10-44 22-44s22 18 22 44M290 568v-14" />
        <path d="M340 612 356 556l16 56M346 590h20M350 574h12" />
        <path d="M400 612v-52M388 612l12-52 12 52M392 592h16M396 576h8" />
        <path d="M20 612h440" />
      </g>
    </svg>
  );
}
